/** Sanitize Clarity event / tag values (single-string event API). */
export const sanitizeClarityToken = (value: string, maxLen = 64): string => {
  const cleaned = value
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^a-zA-Z0-9:_-]/g, "")
    .slice(0, maxLen);
  return cleaned || "unknown";
};

const OPERATION_NAME_RE = /(?:query|mutation|subscription)\s+([A-Za-z0-9_]+)/;

export const parseGraphQLOperationName = (query: string): string => {
  const match = OPERATION_NAME_RE.exec(query);
  return match?.[1] ?? "UnknownOperation";
};

export const parseGraphQLOperationKind = (
  query: string
): "query" | "mutation" | "subscription" | "unknown" => {
  if (/\bmutation\b/i.test(query)) return "mutation";
  if (/\bsubscription\b/i.test(query)) return "subscription";
  if (/\bquery\b/i.test(query)) return "query";
  return "unknown";
};

/**
 * Infer product area / flow from GraphQL operation name for filtering.
 */
export const inferClarityFlow = (operationName: string): string => {
  const o = operationName.toLowerCase();
  // Membership before order — names like CreateMembershipPayment contain "payment"
  if (
    o.includes("membership") ||
    o.includes("subscription") ||
    o.includes("billingcycle") ||
    o.includes("billing_cycle") ||
    o.includes("irembo") ||
    (o.includes("plan") && !o.includes("explain"))
  ) {
    return "membership";
  }
  if (
    o.includes("product") ||
    o.includes("stock") ||
    o.includes("inventory") ||
    o.includes("sku")
  ) {
    return "product";
  }
  if (o.includes("categor") || o.includes("subcategor")) {
    return "category";
  }
  if (
    o.includes("order") ||
    o.includes("invoice") ||
    o.includes("receipt") ||
    o.includes("sales") ||
    o.includes("purchase") ||
    o.includes("customer") ||
    o.includes("supplier") ||
    (o.includes("payment") && !o.includes("membership"))
  ) {
    return "order";
  }
  if (
    o.includes("student") ||
    o.includes("education") ||
    o.includes("grade") ||
    o.includes("program") ||
    o.includes("stream") ||
    o.includes("fee") ||
    o.includes("procurement") ||
    o.includes("requisition") ||
    o.includes("department")
  ) {
    return "education";
  }
  if (
    o.includes("login") ||
    o.includes("logout") ||
    o.includes("authorize") ||
    o.includes("password") ||
    o.includes("otp") ||
    o.includes("register") ||
    o.includes("createaccount") ||
    o.includes("verify")
  ) {
    return "auth";
  }
  if (
    o.includes("employee") ||
    o.includes("company") ||
    o.includes("store") ||
    o.includes("profile")
  ) {
    return "org";
  }
  if (o.includes("assistant") || o.includes("teta")) {
    return "assistant";
  }
  return "other";
};

/**
 * Pull business `code` fields from a GraphQL data payload (HTTP 200 + domain code).
 */
export const extractBusinessCodes = (data: unknown): string[] => {
  if (!data || typeof data !== "object") return [];
  const codes: string[] = [];
  for (const value of Object.values(data as Record<string, unknown>)) {
    if (value && typeof value === "object" && "code" in value) {
      const code = (value as { code?: unknown }).code;
      if (typeof code === "string" && code.length > 0) {
        codes.push(code);
      }
    }
  }
  return codes;
};
